// Wave E — QuickBooks OAuth callback landing page.
//
// Intuit redirects back to the server's /api/v1/integrations/quickbooks/callback,
// which performs the code-for-token exchange and then forwards the user
// to this client-side page. We surface the outcome (presence of `?code` +
// `?state` indicates the server-side exchange succeeded; absence or
// `?error=` indicates failure) and auto-redirect to the status page after
// 2 seconds.
//
// Public route — does NOT sit inside <ProtectedRoute />. The user may be
// arriving from Intuit before the SPA's session is restored.

import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, XCircle } from 'lucide-react';
import styles from './QuickBooksCallbackPage.module.css';

const REDIRECT_DELAY_MS = 2000;
const TARGET_PATH = '/collections/integrations/quickbooks';

export function QuickBooksCallbackPage(): JSX.Element {
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const code = params.get('code');
  const state = params.get('state');
  const errorParam = params.get('error');
  const errorDescription = params.get('error_description');

  const isSuccess = Boolean(code && state) && !errorParam;
  const [countdown, setCountdown] = useState(Math.round(REDIRECT_DELAY_MS / 1000));

  useEffect(() => {
    if (!isSuccess) return;
    const timer = window.setTimeout(() => {
      navigate(TARGET_PATH, { replace: true });
    }, REDIRECT_DELAY_MS);
    const tick = window.setInterval(() => {
      setCountdown((n) => (n > 0 ? n - 1 : 0));
    }, 1000);
    return () => {
      window.clearTimeout(timer);
      window.clearInterval(tick);
    };
  }, [isSuccess, navigate]);

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        {isSuccess ? (
          <>
            <CheckCircle2
              className={styles.iconSuccess}
              size={48}
              aria-hidden
            />
            <h1 className={styles.title}>Successfully connected</h1>
            <p className={styles.body}>
              Your QuickBooks account is now linked to Tricivo. You will be
              redirected back to the integration page shortly.
            </p>
            <p className={styles.countdown} aria-live="polite">
              Redirecting in {countdown}s…
            </p>
            <Link to={TARGET_PATH} className={styles.link}>
              Go now
            </Link>
          </>
        ) : (
          <>
            <XCircle className={styles.iconError} size={48} aria-hidden />
            <h1 className={styles.title}>Connection failed</h1>
            <p className={styles.body}>
              {errorDescription ??
                errorParam ??
                'No authorisation code was received. Please try connecting again.'}
            </p>
            <Link to={TARGET_PATH} className={styles.link}>
              Back to QuickBooks
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
